import React, { memo } from 'react'
import InfoMessage from 'components/InfoMessage'
import Condition from 'components/Condition'
import PageHeader from 'components/PageHeader'
import StyledLink from 'components/StyledLink'
import ConditionSwitch from 'components/ConditionSwitch'
import PageHeaderTitle from 'components/PageHeaderTitle'
import { useRouter } from 'next/router'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { JobHeader_Job$key } from '__generated__/JobHeader_Job.graphql'
import { JobHeader_User$key } from '__generated__/JobHeader_User.graphql'
import { useIsClient, useIsGuest, useIsMember } from 'hooks/user'
import { JobHeader_JobApplication$key } from '__generated__/JobHeader_JobApplication.graphql'
import { useTheme } from '@mui/material/styles'
import { Grid, Button, Typography } from '@mui/material'
import { useMediaQuery } from 'hooks/useMediaQuery'
import useStyles from './styles'

interface IJobHeaderProps {
  jobFragmentRef: JobHeader_Job$key
  userFragmentRef: JobHeader_User$key
  apply: () => void
  fakeApply: () => void
}

function JobHeader({ jobFragmentRef, userFragmentRef, apply, fakeApply }: IJobHeaderProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const router = useRouter()
  const job = useFragment(graphql`
    fragment JobHeader_Job on Job {
      title
      incognito
      jobStatus: status
      jobApplication {
        ...JobHeader_JobApplication
      }
      firm {
        self
        name
        logoUrl
        backgroundTheme
        slug
      }
    }
  `, jobFragmentRef)

  const jobApplication = useFragment<JobHeader_JobApplication$key>(graphql`
    fragment JobHeader_JobApplication on JobApplication {
      status
    }
  `, job?.jobApplication)

  const user = useFragment(graphql`
    fragment JobHeader_User on User {
      status

      ...user_useIsMember
      ...user_useIsGuest
      ...user_useIsClient
    }
  `, userFragmentRef)

  const isMember = useIsMember(user)
  const isClient = useIsClient(user)
  const isGuest = useIsGuest(user)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  if (!job?.firm) return null

  const isLightMode = job.firm?.backgroundTheme === 'light'

  const applicationSent = jobApplication?.status === 'job_application_sent'
  const moreScreening = jobApplication?.status === 'screening_requested' || jobApplication?.status === 'screening_started'
  const isMemberOrGuest = isMember || isGuest
  const notFromDifferentCompany = Boolean(job?.firm?.self)
  const jobClosed = job?.jobStatus === 'closed'
  const displayCta = Boolean(job?.jobStatus === 'opened' && (!applicationSent || moreScreening) && (isMemberOrGuest || notFromDifferentCompany))
  const showCompany = !job?.incognito
  const showAppliedInfoMessage = (applicationSent && !moreScreening) && isMember
  const showJobStatusMessage = job?.jobStatus !== 'opened'
  const showInfoMessage = showAppliedInfoMessage || showJobStatusMessage

  let disabled = jobClosed
  let text = jobClosed ? 'Job closed' : 'Apply Now'
  let action = apply

  if (jobApplication?.status === 'job_application_sent') {
    text = 'Application Sent'
    disabled = true
  }

  if (moreScreening) {
    disabled = false
    text = 'Resume Screening'
    // TODO: preselect the job on the screening tab
    action = () => router.push('/job_search/screening')
  }

  return (
    <PageHeader alternative={isLightMode} white={isLightMode} contentStyle={{ position: 'static' }}>
      <React.Fragment>
        <div className={classes.headerAreaShadeOverlay} />
        <div className={classes.headerAreaCircleOverlay} />
      </React.Fragment>

      <div className={classes.headerArea} data-cy="job-header" style={{ marginTop: isSmallScreen ? theme.spacing(-8) : theme.spacing(2) }}>
        <div className={classes.headerAreaContent}>
          <Grid container spacing={isSmallScreen ? 4 : 2}>
            <Condition condition={showCompany}>
              <Grid item xs={12}>
                <div className={classes.logoWrapper}>
                  {job.firm?.logoUrl && (
                    <img
                      src={job.firm.logoUrl}
                      className={classes.logo}
                      alt={`${job.firm.name} logo`}
                      style={job.firm.backgroundTheme === 'light' ? { boxShadow: `0 0 32px 16px ${'#FFF'}` } : undefined}
                    />
                  )}
                </div>
              </Grid>

              <Grid item xs={12}>
                <PageHeaderTitle>
                  <Typography variant="h4">
                    <StyledLink href={`/${job.firm.slug}`}>{job.firm.name}</StyledLink> is hiring for
                  </Typography>
                </PageHeaderTitle>
              </Grid>
            </Condition>

            <Grid item xs={12}>
              <PageHeaderTitle>
                {job.title}
              </PageHeaderTitle>
            </Grid>

            <Condition condition={displayCta}>
              <Grid item xs={12}>
                <div className={classes.headerCta}>
                  <Condition condition={isClient}>
                    <Button
                      color="primary"
                      size="large"
                      fullWidth
                      data-cy="apply-for-job-fake"
                      onClick={fakeApply}
                    >
                      Apply Now
                    </Button>
                  </Condition>

                  <Condition condition={!isClient}>
                    <Button
                      size="large"
                      color="primary"
                      fullWidth
                      disabled={disabled}
                      onClick={action}
                      data-cy="apply-for-job"
                    >
                      {text}
                    </Button>
                  </Condition>
                </div>
              </Grid>
            </Condition>
          </Grid>

          <Condition condition={showInfoMessage}>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: theme.spacing(4) }}>
              <ConditionSwitch>
                <Condition condition={showAppliedInfoMessage}>
                  <InfoMessage severity="success" data-cy="application-message">
                    Thanks for applying to the position! Your application is currently being reviewed by {job.firm?.name || 'the client'}
                  </InfoMessage>
                </Condition>

                <Condition condition={showJobStatusMessage}>
                  <ConditionSwitch>
                    <Condition condition={job?.jobStatus === 'draft'}>
                      <InfoMessage severity="warning" data-cy="job-draft-message">
                        This position is a draft. When publishing this position, the "Apply" button will appear here.
                      </InfoMessage>
                    </Condition>

                    <Condition condition={job?.jobStatus === 'closed' || job?.jobStatus === 'deleted'}>
                      <InfoMessage severity="error" data-cy="job-closed-message">
                        This position has been closed and is not accepting new applications at this time.
                      </InfoMessage>
                    </Condition>

                    <Condition condition>
                      <InfoMessage severity="error" data-cy="job-unknown-message">
                        This position is not accepting new applications at this time.
                      </InfoMessage>
                    </Condition>
                  </ConditionSwitch>
                </Condition>
              </ConditionSwitch>
            </div>
          </Condition>
        </div>
      </div>
    </PageHeader>
  )
}

export default memo(JobHeader)
